import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { StatusCode } from "api/protocols";
import { valueToString } from "helpers/Helpers";
import { THIS_MONTH, THIS_YEAR } from "components/form/calendarField/helpers";
import Page from "components/page/Page";
import "../facility/facility.scss";
import DataTable from "../../../../../components/dataTable/DataTable";
import axios, { CancelToken } from "axios";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { GetServiceFees, PostServiceFee } from "api/rpc/2024-04/facilityAdmin/payment/serviceFee";
import Sheet from "components/sheet/Sheet";
import { Select } from "components/select/index";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import { showError } from "redux/actions/ui";
import { FACILITY_DISPATCH_TARGET_FACILITY } from "redux/facilityDispatchMeta";

const TABLE_LIMIT = 25;

interface IServiceFeeState {
  serviceFees: any[];
  openCreateServiceFee: boolean;
  type: string;
  amount: number;
  title: string;
  application: string;
}

export default function ServiceFees() {
  const history = useHistory();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { Option } = Select;

  const { facilityStore } = useAppSelector(store => store);

  const [filterState, setFilterState] = useState({
    month: THIS_MONTH,
    year: THIS_YEAR,
    offset: 0,
  });

  const [serviceFeeState, setServiceFeeState] = useState<IServiceFeeState>({
    serviceFees: [],
    openCreateServiceFee: false,
    type: "",
    amount: null,
    title: "",
    application: "",
  });

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadServiceFees(source.token);

    return () => {
      source.cancel();
    };
  }, []);

  async function loadServiceFees(token?: CancelToken) {
    if (serviceFeeState?.serviceFees) {
      setServiceFeeState(prevState => ({ ...prevState, serviceFees: null }));
    }

    const res = await GetServiceFees({ facility_id: facilityStore?.facility?.id }, token ? false : true, token);

    if (token && token.reason) {
      return;
    }

    if (res.status !== StatusCode.OK) {
      setServiceFeeState(prevState => ({ ...prevState, serviceFees: [] }));
      dispatch(showError(t("Error getting facility service fees")));

      return;
    }

    setServiceFeeState(prev => ({
      ...prev,
      serviceFees: res.data,
    }));

    dispatch({
      type: "facility.update",
      meta: { facilityDispatchTarget: FACILITY_DISPATCH_TARGET_FACILITY },
      payload: {
        isLoaded: true,
        serviceFees: res.data,
      },
    });
  }

  async function handleCreateServiceFee() {
    const decimal = serviceFeeState?.amount / 100;

    // Build a safe struct inline
    const params =
      serviceFeeState?.type === "fixed_amount"
        ? {
            type: "fixed_amount",
            fixed_amount: serviceFeeState?.amount,
            percent: null,
            title: serviceFeeState?.title,
            application: "subtotal_price",
          }
        : serviceFeeState?.type === "percent"
        ? {
            type: "percent",
            percent: decimal,
            fixed_amount: null,
            title: serviceFeeState?.title,
            application: serviceFeeState?.application,
          }
        : {};

    const res = await PostServiceFee(params, true);

    if (res.status !== StatusCode.OK) {
      setServiceFeeState(prevState => ({ ...prevState, serviceFees: [] }));
      return;
    }

    setServiceFeeState(prevState => ({ ...prevState, openCreateServiceFee: false, title: "", amount: null, type: "" }));
    void loadServiceFees();
  }

  function handleTableNavigation(direction: "prev" | "next") {
    switch (direction) {
      case "prev": {
        setFilterState(prev => ({ ...prev, offset: filterState.offset - TABLE_LIMIT }));
        return;
      }
      case "next": {
        setFilterState(prev => ({ ...prev, offset: filterState.offset + TABLE_LIMIT }));
        return;
      }
      default:
        return;
    }
  }

  const primaryAction = {
    content: t("Create Service Fee"),
    action: () => handleOpenCreateServiceFee(),
  };

  function handleOpenCreateServiceFee() {
    setServiceFeeState(prevState => ({ ...prevState, openCreateServiceFee: true }));
  }

  function navigateToViewServiceFee(url: string) {
    history.push(url);
  }

  return (
    <Page title={t("Service Fees")} primaryAction={primaryAction}>
      <DataTable
        loading={!serviceFeeState?.serviceFees ? true : false}
        columns={[{ label: t("Title") }, { label: t("Type") }, { label: t("Amount / Percent") }]}
        footer={{
          tableLimit: TABLE_LIMIT,
          tableOffset: filterState.offset,
          disableNextOffset: !serviceFeeState?.serviceFees || !(serviceFeeState?.serviceFees?.length === TABLE_LIMIT),
          handleTableOffset: direction => handleTableNavigation(direction),
        }}
      >
        {serviceFeeState?.serviceFees?.map((fee: any, index: any) => {
          const percentage = `${(fee?.percent * 100).toFixed(2)}%`;
          return (
            <tr
              key={index}
              className="clickable"
              onClick={() => navigateToViewServiceFee("/admin/settings/service-fees/" + String(fee.id))}
            >
              <td>{fee?.title}</td>
              <td>{valueToString(String(fee?.type))}</td>
              <td>{fee?.type === "fixed_amount" ? "$" + fee?.fixed_amount : percentage}</td>
            </tr>
          );
        })}
      </DataTable>

      {serviceFeeState?.openCreateServiceFee && (
        <Sheet
          open={serviceFeeState?.openCreateServiceFee}
          title={t("Create Service Fee")}
          size="small"
          okText={t("Create")}
          onOk={handleCreateServiceFee}
          okDisabled={serviceFeeState?.type === "" || serviceFeeState?.amount === null || serviceFeeState?.amount <= 0}
          onCancel={() =>
            setServiceFeeState(prevState => ({
              ...prevState,
              openCreateServiceFee: false,
              amount: null,
              type: "",
              title: "",
            }))
          }
          cancelText={t("Cancel")}
          overflow
        >
          <FormLayout>
            <FormLayout.Group>
              <Input
                id="title"
                value={serviceFeeState?.title}
                onChange={e => setServiceFeeState(prev => ({ ...prev, title: e.target.value }))}
                label={t("Title")}
                // disabled={readOnly || product?.warehouse_product_id === null}
              />

              <Select
                onChange={(value: number) => setServiceFeeState(prev => ({ ...prev, type: String(value) }))}
                defaultValue={serviceFeeState?.type}
                className="type-select"
                label={t("Type")}
              >
                <Option value={"fixed_amount"}>{t("Fixed")}</Option>
                <Option value={"percent"}>{t("Percent")}</Option>
              </Select>
            </FormLayout.Group>
            <FormLayout.Group>
              <Input
                id="amount"
                value={serviceFeeState?.amount}
                onChange={e => setServiceFeeState(prev => ({ ...prev, amount: e.target.valueAsNumber }))}
                label={t("Amount")}
                // disabled={readOnly || product?.warehouse_product_id === null}
                type={"number"}
              />

              {serviceFeeState?.type === "percent" ? (
                <Select
                  onChange={(value: number) => setServiceFeeState(prev => ({ ...prev, application: String(value) }))}
                  defaultValue={serviceFeeState?.application}
                  className="type-select"
                  label={t("Application")}
                >
                  <Option value={"total_price"}>{t("Total Price")}</Option>
                  <Option value={"subtotal_price"}>{t("Subtotal Price")}</Option>
                </Select>
              ) : null}
            </FormLayout.Group>
          </FormLayout>
        </Sheet>
      )}
    </Page>
  );
}
